/**
 * Titles: the words under a child's name ([D-159](../../../docs/DECISIONS.md#d-159),
 * mockup 25, [Economy](../../../docs/ECONOMY.md#titles)).
 *
 * Theirs first, one tap to wear; then the ones to save up for; then the ones
 * a big moment hands over for free, each saying which badge does it. The only
 * words drawn are the catalogue's, sent by id — a title is the one thing a
 * sibling sees at sign-in, so it is never text a client made up.
 */

import '@capytalk/tokens/blocks/chapter.css';
import { refusalText, refuse } from '@capytalk/core';
import { useEffect, useState } from 'react';
import type { Client } from './api.js';
import Sneakers from './Sneakers.js';

type Shelf = Awaited<ReturnType<Client['shop']['titles']['query']>>;
type Title = Shelf['titles'][number];

export default function Titles({
  client,
  onDone,
}: {
  readonly client: Client;
  /** With the words now worn, or null for none. */
  readonly onDone: (wearing: string | null) => void;
}) {
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [trouble, setTrouble] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => client.shop.titles.query())
      .then((found) => {
        if (live) setShelf(found);
      })
      .catch(() => {
        if (live) setTrouble(true);
      });
    return () => {
      live = false;
    };
  }, [client]);

  const wear = (titleId: string | null) => {
    if (busy) return;
    setBusy(true);
    Promise.resolve()
      .then(() => client.shop.wearTitle.mutate({ titleId }))
      .then((next) => {
        setShelf(next);
        setSaid(
          titleId === null ? 'No title for now. That is fine too!' : 'Looks good!',
        );
      })
      .catch(() => {
        setSaid('That did not work. Try again in a moment.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const buy = (title: Title) => {
    if (busy) return;
    setBusy(true);
    setSaid(null);
    Promise.resolve()
      .then(() => client.shop.buyTitle.mutate({ titleId: title.id }))
      .then((bought) => {
        setShelf((was) =>
          was === null
            ? was
            : {
                ...was,
                acorns: bought.acorns,
                titles: was.titles.map((one) =>
                  one.id === title.id && bought.refused === null
                    ? { ...one, owned: true }
                    : one,
                ),
              },
        );
        setSaid(
          bought.refused === null
            ? `“${title.text}” is yours! Tap it to wear it.`
            : refusalText[bought.refused],
        );
      })
      .catch(() => {
        setSaid('That did not work. Try again in a moment.');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const back = () => {
    const worn = shelf?.titles.find((one) => one.id === shelf.wearing);
    onDone(worn?.text ?? null);
  };

  if (trouble) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          The titles would not load. Please try again.
        </p>
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 12 }}
          onClick={back}
        >
          Back
        </button>
      </div>
    );
  }

  if (shelf === null) {
    return (
      <div className="grow pad center">
        <p className="sub">One moment…</p>
      </div>
    );
  }

  const mine = shelf.titles.filter((one) => one.owned);
  const forSale = shelf.titles.filter((one) => !one.owned && one.price !== null);
  const toEarn = shelf.titles.filter((one) => !one.owned && one.price === null);

  return (
    <div className="grow pad" style={{ paddingTop: 8 }}>
      <h2>Titles</h2>
      <div className="tiny" style={{ marginTop: 3 }}>
        Shown under your name
      </div>
      <div style={{ marginTop: 10 }}>
        <Sneakers
          text={said ?? 'Which one feels like you?'}
          audioRef={null}
          size={48}
        />
      </div>
      <p className="sub" style={{ marginTop: 8 }}>
        <span aria-hidden="true">🌰</span> {shelf.acorns.toLocaleString('en-GB')} acorns
      </p>

      {mine.length > 0 && (
        <section aria-label="Your titles" style={{ marginTop: 12 }}>
          <div className="kicker" style={{ marginBottom: 9 }}>
            YOURS — TAP ONE TO WEAR IT
          </div>
          {mine.map((title) => {
            const on = shelf.wearing === title.id;
            return (
              <button
                key={title.id}
                type="button"
                className="opt"
                aria-pressed={on}
                disabled={busy}
                style={{
                  width: '100%',
                  font: 'inherit',
                  cursor: 'pointer',
                  ...(on
                    ? { borderColor: 'var(--navy2)', boxShadow: '0 3px 0 var(--navy2)' }
                    : {}),
                }}
                onClick={() => {
                  wear(on ? null : title.id);
                }}
              >
                <span style={{ flex: 1, textAlign: 'left' }}>
                  <span className="title-badge">{title.text}</span>
                </span>
                {on && (
                  <span
                    className="tiny"
                    style={{ fontWeight: 800, color: 'var(--navy2)' }}
                  >
                    WEARING
                  </span>
                )}
              </button>
            );
          })}
          {shelf.wearing !== null && (
            <p className="tiny">Tap the one you are wearing to take it off.</p>
          )}
        </section>
      )}

      {forSale.length > 0 && (
        <section aria-label="Titles to buy" style={{ marginTop: 14 }}>
          <div className="kicker" style={{ marginBottom: 9 }}>
            BUY WITH ACORNS
          </div>
          {forSale.map((title) => {
            const no = refuse({ price: title.price ?? 0, owned: false }, shelf.acorns);
            return (
              <div key={title.id} className="opt">
                <span style={{ flex: 1 }}>
                  <span className="title-badge" style={{ opacity: 0.55 }}>
                    {title.text}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn"
                  style={{ width: 'auto', padding: '12px 14px', minHeight: 48 }}
                  disabled={busy || no !== null}
                  aria-label={`Buy ${title.text} for ${String(title.price)} acorns`}
                  onClick={() => {
                    buy(title);
                  }}
                >
                  <span aria-hidden="true">🌰</span>{' '}
                  {(title.price ?? 0).toLocaleString('en-GB')}
                </button>
              </div>
            );
          })}
        </section>
      )}

      <div
        className="card"
        style={{ marginTop: 6, background: '#EDF6EA', borderColor: '#BFE0B6' }}
      >
        <div className="tiny" style={{ fontWeight: 700, color: '#3F8635' }}>
          You also earn free titles for big moments — like finishing a book.
        </div>
      </div>

      {toEarn.length > 0 && (
        <section aria-label="Titles to earn" style={{ marginTop: 14 }}>
          <div className="kicker" style={{ marginBottom: 9 }}>
            EARN FOR FREE
          </div>
          {toEarn.map((title) => (
            <div key={title.id} className="opt">
              <span style={{ flex: 1 }}>
                <span className="title-badge" style={{ opacity: 0.55 }}>
                  {title.text}
                </span>
              </span>
              {title.earnedBy !== null && (
                <span className="tiny" style={{ fontWeight: 700, textAlign: 'right' }}>
                  <span aria-hidden="true">🏅</span> {title.earnedBy}
                </span>
              )}
            </div>
          ))}
        </section>
      )}

      <button
        type="button"
        className="btn ghost"
        style={{ marginTop: 10, marginBottom: 20 }}
        onClick={back}
      >
        Back
      </button>
    </div>
  );
}
